import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Mail, X, ShieldCheck, Loader2, Sparkles, Trash2, ArrowRight } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";

/**
 * FamilySection — a single component that renders EITHER the student's
 * "Add a parent/guardian" panel OR the parent's "Your linked kids" panel,
 * depending on the current user's role.
 *
 * Placed on `/account` and only shown to school-verified students or parents.
 */
export function FamilySection({ user, onActiveChildChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);

  const isParent = user?.role === "parent";
  const isStudent = user?.role !== "parent" && user?.role !== "admin";

  const load = async () => {
    try {
      const { data } = await api.familyMine();
      setData(data);
    } catch { /* noop */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (linkId, name) => {
    if (!window.confirm(`Remove ${name} from your family?`)) return;
    try {
      await api.familyRemove(linkId);
      toast.success(`Removed ${name}.`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const shopFor = async (studentId, studentName) => {
    try {
      await api.familySetActiveChild(studentId);
      localStorage.setItem("hb_active_child_id", studentId || "");
      localStorage.setItem("hb_active_child_name", studentName || "");
      window.dispatchEvent(new Event("hb-active-child-changed"));
      onActiveChildChange?.(studentId, studentName);
      toast.success(`Now shopping for ${studentName}.`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (loading) return <div className="border border-border rounded-sm p-8 flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-rosemetal" /></div>;

  // --- STUDENT VIEW ---
  if (isStudent) {
    if (!user.school_verified) {
      return (
        <div data-testid="family-section" className="border border-border rounded-sm p-6 bg-blush/30">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-rosemetal/20 flex items-center justify-center shrink-0">
              <Users className="w-4 h-4 text-rosemetal" />
            </div>
            <div>
              <p className="font-display text-2xl leading-tight">Bring a parent along</p>
              <p className="font-body text-sm text-ink/70 mt-1">Verify your school email first, and you'll be able to invite a parent or guardian to help with checkout.</p>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div data-testid="family-section" className="border border-border rounded-sm p-6 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-label text-[10px] text-rosemetal tracking-widest">FAMILY</p>
            <h3 className="font-display text-3xl mt-1 leading-tight">Add a parent or guardian</h3>
            <p className="font-body text-sm text-ink/60 mt-2 max-w-md">Let them help you check out. Locks, points, and try-ons stay on your account — they just handle the payment when you need it.</p>
          </div>
          <button
            data-testid="add-parent-btn"
            onClick={() => setInviting(true)}
            className="bg-ink text-ivory font-label text-[10px] tracking-[0.25em] px-5 py-3 rounded-sm hover:bg-rosemetal transition-colors flex items-center gap-2 shrink-0"
          >
            <Mail className="w-3.5 h-3.5" /> INVITE
          </button>
        </div>

        {data?.parents?.length > 0 && (
          <div>
            <p className="font-label text-[9px] tracking-widest text-ink/50 mb-2">LINKED FAMILY</p>
            <div className="space-y-2">
              {data.parents.map((p) => (
                <div key={p.link_id} data-testid={`family-parent-${p.link_id}`} className="flex items-center justify-between border border-border rounded-sm p-3">
                  <div className="min-w-0">
                    <p className="font-body text-sm text-ink truncate">{p.name || p.email}</p>
                    <p className="font-label text-[9px] tracking-widest text-ink/50 mt-0.5">{(p.relationship || "PARENT").toUpperCase()} · {p.email}</p>
                  </div>
                  <button onClick={() => remove(p.link_id, p.name || p.email)} className="text-ink/40 hover:text-rosemetal p-2" data-testid={`family-remove-${p.link_id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {data?.pending_invites?.length > 0 && (
          <div>
            <p className="font-label text-[9px] tracking-widest text-ink/50 mb-2">PENDING INVITES</p>
            <div className="space-y-2">
              {data.pending_invites.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between border border-dashed border-rosemetal/40 rounded-sm p-3 bg-blush/20">
                  <p className="font-body text-sm text-ink/70 truncate">{inv.parent_email}</p>
                  <span className="font-label text-[9px] tracking-widest text-rosemetal">WAITING</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <InviteParentModal open={inviting} onClose={() => setInviting(false)} onSent={load} />
      </div>
    );
  }

  // --- PARENT VIEW ---
  if (isParent) {
    const activeId = localStorage.getItem("hb_active_child_id") || "";
    return (
      <div data-testid="family-section" className="border border-border rounded-sm p-6 space-y-5 bg-blush/20">
        <div>
          <p className="font-label text-[10px] text-rosemetal tracking-widest">MY FAMILY</p>
          <h3 className="font-display text-3xl mt-1 leading-tight">Shop for your student</h3>
          <p className="font-body text-sm text-ink/60 mt-2 max-w-md">Pick a linked student to shop for — locks and school points still credit to them.</p>
        </div>
        {data?.children?.length === 0 ? (
          <div className="border border-dashed border-ink/20 rounded-sm p-6 text-center">
            <p className="font-body text-sm text-ink/60">No linked students yet. Ask your student to invite you from their account.</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {data.children.map((c) => (
              <div key={c.link_id} data-testid={`family-child-${c.link_id}`} className={`border rounded-sm p-4 flex items-center justify-between gap-4 transition-colors ${activeId === c.id ? "border-rosemetal bg-ivory" : "border-border bg-ivory"}`}>
                <div className="min-w-0">
                  <p className="font-display text-xl text-ink">{c.name}</p>
                  <p className="font-label text-[9px] tracking-widest text-ink/50 mt-1">
                    {(c.relationship || "STUDENT").toUpperCase()}{c.school_name ? ` · ${c.school_name}` : ""}
                    {!c.school_verified && <span className="text-rosemetal"> · NEEDS TO VERIFY</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link to={`/family/lookbook/${c.id}`} data-testid={`view-lookbook-${c.link_id}`} className="font-label text-[10px] tracking-widest text-ink/70 hover:text-rosemetal px-3 py-2 border border-border rounded-sm">LOOKBOOK</Link>
                  <button
                    onClick={() => shopFor(c.id, c.name)}
                    data-testid={`shop-for-${c.link_id}`}
                    className={`font-label text-[10px] tracking-[0.25em] px-4 py-2.5 rounded-sm transition-colors flex items-center gap-2 ${activeId === c.id ? "bg-champagne text-ink" : "bg-ink text-ivory hover:bg-rosemetal"}`}
                  >
                    {activeId === c.id ? <ShieldCheck className="w-3.5 h-3.5" /> : <ArrowRight className="w-3.5 h-3.5" />}
                    {activeId === c.id ? "ACTIVE" : "SHOP FOR"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="font-label text-[9px] tracking-widest text-ink/40">AI TRY-ON IS STUDENT-ONLY · YOU CAN'T GENERATE LOOKS ON THEIR BEHALF</p>
      </div>
    );
  }

  return null;
}

function InviteParentModal({ open, onClose, onSent }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [relationship, setRelationship] = useState("parent");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.familyInvite({ parent_email: email.trim(), parent_name: name.trim() || undefined, relationship });
      setSent(true);
      onSent?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  const close = () => {
    setEmail(""); setName(""); setRelationship("parent"); setSent(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={close}
          className="fixed inset-0 z-[70] bg-ink/70 backdrop-blur-md flex items-center justify-center p-4"
          data-testid="invite-parent-modal"
        >
          <motion.div
            initial={{ y: 20, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 20, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="bg-ivory rounded-sm w-full max-w-md relative"
          >
            <button onClick={close} className="absolute top-3 right-3 text-ink/60 hover:text-ink" data-testid="invite-modal-close"><X className="w-5 h-5" /></button>
            <div className="p-8">
              {sent ? (
                <div className="text-center py-6" data-testid="invite-sent">
                  <div className="mx-auto w-14 h-14 rounded-full bg-champagne flex items-center justify-center mb-4">
                    <Sparkles className="w-6 h-6 text-ink" />
                  </div>
                  <p className="font-display text-3xl leading-tight">Invite sent ✨</p>
                  <p className="font-body text-sm text-ink/60 mt-3 max-w-xs mx-auto">We just emailed <b>{email}</b> a magic-link invite. They can tap it to set up their account and start helping you shop.</p>
                  <button onClick={close} className="mt-6 bg-ink text-ivory font-label text-[10px] tracking-[0.25em] px-6 py-3 rounded-sm hover:bg-rosemetal transition-colors">DONE</button>
                </div>
              ) : (
                <>
                  <p className="font-label text-[10px] tracking-widest text-rosemetal">INVITE A GROWN-UP</p>
                  <h3 className="font-display text-3xl leading-tight mt-2 mb-2">Add a parent or guardian</h3>
                  <p className="font-body text-sm text-ink/60 mb-6">We'll email them a magic link. They'll set up their account and can help you check out — no school email required for parents.</p>
                  <form onSubmit={submit} className="space-y-3">
                    <div>
                      <label className="font-label text-[9px] tracking-widest text-ink/60">EMAIL</label>
                      <input
                        type="email" required autoFocus
                        value={email} onChange={(e) => setEmail(e.target.value)}
                        placeholder="mom@yourfamily.com"
                        data-testid="invite-email"
                        className="mt-1 w-full border border-border rounded-sm px-4 py-3 font-body text-sm outline-none focus:border-rosemetal transition-colors"
                      />
                    </div>
                    <div>
                      <label className="font-label text-[9px] tracking-widest text-ink/60">THEIR NAME (OPTIONAL)</label>
                      <input
                        type="text"
                        value={name} onChange={(e) => setName(e.target.value)}
                        placeholder="Mom"
                        data-testid="invite-name"
                        className="mt-1 w-full border border-border rounded-sm px-4 py-3 font-body text-sm outline-none focus:border-rosemetal transition-colors"
                      />
                    </div>
                    <div>
                      <label className="font-label text-[9px] tracking-widest text-ink/60">RELATIONSHIP</label>
                      <select
                        value={relationship} onChange={(e) => setRelationship(e.target.value)}
                        data-testid="invite-relationship"
                        className="mt-1 w-full border border-border rounded-sm px-4 py-3 font-body text-sm outline-none focus:border-rosemetal bg-ivory"
                      >
                        <option value="parent">Parent</option>
                        <option value="mom">Mom</option>
                        <option value="dad">Dad</option>
                        <option value="guardian">Guardian</option>
                        <option value="grandparent">Grandparent</option>
                      </select>
                    </div>
                    <button
                      type="submit" disabled={busy}
                      data-testid="invite-submit"
                      className="w-full bg-ink text-ivory font-label text-[11px] tracking-[0.25em] py-4 rounded-sm hover:bg-rosemetal transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                      {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> SENDING…</> : <><Mail className="w-4 h-4" /> SEND INVITE</>}
                    </button>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
